<template>
    <div class="flood-profile" ref="root">
        <div v-if="state === 'loading'" class="placeholder-glow" aria-hidden="true">
            <div class="row g-4">
                <div class="col-lg-6">
                    <span class="placeholder flood-profile__placeholder w-100 rounded mb-3"></span>
                    <span class="placeholder col-8 mb-2"></span>
                    <span class="placeholder col-6"></span>
                </div>
                <div class="col-lg-6">
                    <span class="placeholder col-7 mb-2"></span>
                    <span class="placeholder col-9 mb-2"></span>
                    <span class="placeholder col-8"></span>
                </div>
            </div>
        </div>
        <p v-else-if="state === 'failed' || profile === null" class="text-muted mb-0">Údaje o povodňovém riziku se nepodařilo načíst.</p>
        <template v-else>
            <div v-if="imprecision !== null" class="alert alert-warning py-2 mb-3">
                <i class="fas fa-exclamation-triangle me-2"></i>{{ imprecision }}
            </div>
            <div class="row g-4">
                <div class="col-lg-6">
                    <div class="flood-profile__hero" :style="{'--flood-risk-color': risk.color}">
                        <div class="flood-profile__level">
                            <span class="flood-profile__level-value">{{ risk.label }}</span>
                            <span class="flood-profile__level-caption">nebezpečí povodně<br>parametr „Možnosti záplav“</span>
                        </div>
                        <div class="flood-profile__summary">{{ summary }}</div>
                        <div class="flood-profile__scale">
                            <div v-for="step in scale" :key="step.key"
                                 class="flood-profile__scale-step" :class="{'flood-profile__scale-step--active': step.active}"
                                 :style="{'--step-color': step.color}">{{ step.label }}</div>
                        </div>
                    </div>

                    <table class="flood-profile__facts">
                        <tbody>
                            <tr>
                                <th>Záplavová území</th>
                                <td v-if="!profile.floodZonesAvailable" class="flood-profile__muted">{{ dataUnavailable }}</td>
                                <td v-else>
                                    <span v-for="zone in zones" :key="zone.key" class="flood-profile__zone"
                                          :class="{'flood-profile__zone--outside': !zone.reaches}" :style="{'--zone-color': zone.color}"
                                          :title="zone.reaches ? 'voda dosahuje k místu' : 'voda k místu nedosahuje'">{{ zone.label }}</span>
                                    <div v-if="profile.streamName" class="flood-profile__muted">tok {{ profile.streamName }}</div>
                                    <div v-else-if="profile.distanceToQ100Metres !== null" class="flood-profile__muted">
                                        nejbližší hranice 100leté vody {{ distanceToQ100 }}
                                    </div>
                                    <div v-else class="flood-profile__muted">žádné stanovené záplavové území do 5 km</div>
                                </td>
                            </tr>
                            <tr>
                                <th>Aktivní zóna</th>
                                <td v-if="!profile.floodZonesAvailable" class="flood-profile__muted">{{ dataUnavailable }}</td>
                                <td v-else>
                                    <template v-if="inActiveZone"><strong>ano</strong> – v aktivní zóně záplavového území platí zákaz staveb podle vodního zákona</template>
                                    <span v-else class="flood-profile__muted">ne</span>
                                </td>
                            </tr>
                            <tr>
                                <th>Povodňové ohrožení</th>
                                <td>
                                    <template v-if="!profile.hazardMapAvailable">
                                        <span class="flood-profile__muted">mapa ohrožení pro tuto lokalitu není zpracována</span>
                                    </template>
                                    <template v-else-if="hazard !== null">
                                        <span class="flood-profile__swatch" :style="{backgroundColor: hazard.color}"></span><strong>{{ hazard.label }}</strong>
                                        <span class="flood-profile__muted"> (stav {{ profile.hazardMapVersion }})</span>
                                    </template>
                                    <template v-else>
                                        mimo zaplavované území <span class="flood-profile__muted">(stav {{ profile.hazardMapVersion }})</span>
                                    </template>
                                </td>
                            </tr>
                            <tr>
                                <th>Zaznamenané povodně</th>
                                <td v-if="!profile.floodZonesAvailable" class="flood-profile__muted">{{ dataUnavailable }}</td>
                                <td v-else>
                                    <template v-if="floods.length > 0">{{ floods.join(', ') }}</template>
                                    <span v-else class="flood-profile__muted">žádná z evidovaných povodní (1880–2013) místo nezaplavila</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="col-lg-6">
                    <ul class="flood-profile__help list-unstyled mb-3">
                        <li v-for="item in help" :key="item.lead" class="mb-2">
                            <strong>{{ item.lead }}</strong> {{ item.text }}
                        </li>
                    </ul>
                    <div class="flood-profile__map-cta">
                        <div class="flood-profile__map-cta-title">Jak vypadá povodňová mapa okolí?</div>
                        <div class="flood-profile__map-cta-text">
                            Záplavová území, aktivní zóna a kategorie ohrožení nad leteckým snímkem místa.
                        </div>
                        <button v-if="unfoldMapId !== null" type="button" class="btn btn-primary" data-bs-toggle="collapse"
                                :data-bs-target="'#' + unfoldMapId" :aria-controls="unfoldMapId" aria-expanded="false">
                            <i class="fas fa-map-marker-alt me-2"></i>Zobrazit povodňovou mapu okolí
                        </button>
                        <button v-else type="button" class="btn btn-primary" @click="openMap">
                            <i class="fas fa-map-marker-alt me-2"></i>Otevřít povodňovou mapu okolí
                        </button>
                    </div>
                    <div class="flood-profile__attribution">{{ disclaimer }} {{ attribution }}</div>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import {
    ATTRIBUTION,
    DISCLAIMER,
    FLOOD_ZONE_LABELS,
    FLOOD_ZONE_LABELS_GENITIVE,
    FLOOD_ZONE_SCENARIOS,
    FLOOD_ZONES_UNAVAILABLE,
    FLOODING_RISK_ORDER,
    FLOODING_RISKS,
    FloodRiskProfile,
    floodZonesReaching,
    formatMetres,
    HAZARD_CATEGORIES,
    historicalFloods,
    SCENARIOS,
} from '@/components/flood/floodProfile';
import {loadFloodProfile} from '@/model/flood/floodProfile';
import naja from 'naja';
import {whenNearViewport} from '@/shared/nearViewport';
import {computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';

type LoadState = 'loading' | 'loaded' | 'failed';

// The hero figure and the facts of the designated flood areas while those are not imported: grey,
// with no step of the scale lit, rather than a place called safe for want of data.
const UNKNOWN_RISK = {label: 'nelze určit', color: '#999999'};
const DATA_UNAVAILABLE = 'údaje nejsou k dispozici';

// A place known only as a street or a larger unit is described by the centre of that unit; the
// flood zones are a matter of metres, so the profile says what the point stands for.
const IMPRECISE_LOCATIONS: Record<string, string> = {
    street: 'Poloha nemovitosti je známa jen na úroveň ulice, hodnotí se tedy střed ulice. Skutečné místo může ležet jinde a jeho povodňové riziko se může lišit.',
    'town-part': 'Poloha nemovitosti je známa jen na úroveň části obce, hodnotí se tedy její střed. Skutečné místo může ležet jinde a jeho povodňové riziko se může lišit.',
    'cadastral-unit': 'Poloha nemovitosti je známa jen na úroveň katastrálního území, hodnotí se tedy jeho střed. Skutečné místo může ležet jinde a jeho povodňové riziko se může lišit.',
    town: 'Poloha nemovitosti je známa jen na úroveň obce, hodnotí se tedy její střed. Skutečné místo může ležet jinde a jeho povodňové riziko se může lišit.',
};

// What the layers are and what they are not, in the words of the profile.
const HELP: ReadonlyArray<{lead: string; text: string}> = [
    {
        lead: 'Záplavová území',
        text: 'Stanovená vodoprávními úřady pro pětiletou, dvacetiletou, stoletou a pětisetletou vodu; nebezpečí povodně '
            + 'je dané nejčastější vodou, která k místu dosáhne. Průběh hranic je orientační, závazné je rozhodnutí úřadu.',
    },
    {
        lead: 'Povodňové ohrožení',
        text: 'Z map povodňového nebezpečí podle evropské směrnice: hydraulický model dělí zaplavované území podle hloubky '
            + 'a rychlosti vody do čtyř stupňů (zbytkové, nízké, střední, vysoké). Zpracováno jen pro úseky toků s významným '
            + 'povodňovým rizikem; rok uvádí stav plánovacího cyklu.',
    },
    {
        lead: 'Co údaje neříkají',
        text: 'Místo mimo stanovená území není bez rizika: malé toky bez stanoveného území a přívalové povodně nejsou pokryté. '
            + 'Pojišťovny používají vlastní povodňové mapy, které se od těchto mohou lišit.',
    },
];

/**
 * The flood risk profile of a place without a map: the flooding risk as the hero figure with the
 * scale of the parameter, the facts of every flood layer in a table, a short explanation of the
 * layers and a call to open the flood map of the surroundings: in a dialog, or, where the widget
 * itself sits in a dialog, by unfolding the map (a collapse of the given id) under the widget.
 */
export default defineComponent({
    props: {
        place: {
            type: Object as PropType<{latitude: number; longitude: number}>,
            required: true,
        },
        // The kind of place the point stands for (address-place, street, town-part, cadastral-unit, town).
        locationType: {
            type: String,
            default: 'address-place',
        },
        openMapUrl: {
            type: String,
            default: null,
        },
        unfoldMapId: {
            type: String,
            default: null,
        },
    },
    setup(props) {
        const state = ref<LoadState>('loading');
        const profile = ref<FloodRiskProfile | null>(null);

        const imprecision = computed((): string | null => IMPRECISE_LOCATIONS[props.locationType] ?? null);

        const risk = computed(() => {
            const floodingRisk = profile.value?.floodingRisk ?? null;
            return floodingRisk !== null ? FLOODING_RISKS[floodingRisk] ?? UNKNOWN_RISK : UNKNOWN_RISK;
        });

        const scale = computed(() => FLOODING_RISK_ORDER.map((key) => ({
            key,
            label: FLOODING_RISKS[key].label,
            color: FLOODING_RISKS[key].color,
            active: profile.value?.floodingRisk === key,
        })));

        const zones = computed(() => {
            const reaching = profile.value !== null ? floodZonesReaching(profile.value) : [];
            return FLOOD_ZONE_SCENARIOS.map((key) => ({
                key,
                label: `${FLOOD_ZONE_LABELS[key]} voda`,
                color: SCENARIOS[key].color,
                reaches: reaching.includes(key),
            }));
        });

        const summary = computed((): string => {
            if (profile.value === null) {
                return '';
            }
            if (!profile.value.floodZonesAvailable) {
                return FLOOD_ZONES_UNAVAILABLE;
            }
            const reaching = floodZonesReaching(profile.value);
            if (reaching.length === 0) {
                return 'Místo leží mimo stanovená záplavová území.';
            }
            const stream = profile.value.streamName ? ` toku ${profile.value.streamName}` : '';
            return `Místo leží v záplavovém území ${FLOOD_ZONE_LABELS_GENITIVE[reaching[0]]} vody${stream}.`;
        });

        const inActiveZone = computed((): boolean => profile.value?.scenarios.includes('active_zone') ?? false);
        const hazard = computed(() => (profile.value?.hazardCategory != null ? HAZARD_CATEGORIES[profile.value.hazardCategory] ?? null : null));
        const floods = computed((): string[] => (profile.value !== null ? historicalFloods(profile.value) : []));
        const distanceToQ100 = computed((): string => (
            profile.value?.distanceToQ100Metres != null ? formatMetres(profile.value.distanceToQ100Metres) : ''
        ));

        function openMap(): void {
            if (props.openMapUrl === null) {
                return;
            }
            naja.makeRequest('GET', props.openMapUrl, null, {history: false});
        }

        async function load(): Promise<void> {
            try {
                profile.value = await loadFloodProfile(props.place.latitude, props.place.longitude);
                state.value = 'loaded';
            } catch {
                state.value = 'failed';
            }
        }

        // Loaded only once the widget is about to come into view: most visitors of a page never
        // scroll down to it.
        const root = ref<HTMLElement | null>(null);
        let stopWatchingViewport: (() => void) | null = null;

        onMounted(() => {
            stopWatchingViewport = whenNearViewport(root.value!, load);
        });

        onBeforeUnmount(() => stopWatchingViewport?.());

        return {
            root, state, profile, imprecision, risk, scale, zones, summary, inActiveZone, hazard, floods, distanceToQ100,
            help: HELP, attribution: ATTRIBUTION, disclaimer: DISCLAIMER, dataUnavailable: DATA_UNAVAILABLE, openMap,
        };
    },
});
</script>
